import ApiActions from '../actions/api';
import UserSession from './user-session';
import { getDevicePlatform } from '../helpers/devicePlatform';
import { logInfo } from '../analytics/logger/logger';

/**
 * Mints a single-use handoff (web login) refresh token for the in-app browser
 * account-creation flow. This is requested at the LAST onboarding step, right
 * before the native deep-link redirect, so that it cannot be invalidated by any
 * completion logout that may run earlier in the flow.
 *
 * The token is a refresh-token credential, so it is never logged or persisted —
 * it is returned to the caller and immediately appended to the deep link.
 *
 * @returns the handoff token string, or null if it could not be minted.
 */
export async function requestHandoffToken(): Promise<string | null> {
  const user = UserSession.getUserData();
  const userId = user && user.user_id;
  if (!userId) {
    logInfo('[handoff] cannot mint handoff token: no authenticated user');
    return null;
  }

  try {
    const response = await ApiActions.post(
      {
        entity: 'UserAccountAuthentication',
        method: 'create_temporary_web_login_token',
        serviceVersion: '1',
        data: {
          user_id: userId,
          product_id: window.product_id || 1,
          device_platform: getDevicePlatform(),
          token_duration: null,
        },
      },
      true
    );

    const token =
      response && typeof response.result === 'string' ? response.result : null;
    // [handoff] diagnostics: never log the token value (refresh-token credential).
    // Note: the do-ajax layer resolves (does not reject) on non-2xx, surfacing the
    // proxy/service status in the resolved body (e.g. { result: 'Forbidden',
    // status_code: 403 }), so capture that here too rather than only in catch.
    logInfo('[handoff] requestHandoffToken result', {
      tokenCreated: Boolean(token),
      tokenLength: typeof token === 'string' ? token.length : 0,
      devicePlatform: getDevicePlatform(),
      responseStatus:
        typeof response?.status === 'number' ? response.status : null,
      responseStatusCode:
        typeof response?.status_code === 'number' ? response.status_code : null,
    });
    return token;
  } catch (exception) {
    const err = (exception ?? {}) as {
      status?: unknown;
      status_code?: unknown;
      response?: { status?: unknown };
    };
    const status = err.status ?? err.response?.status ?? err.status_code;
    // [handoff] never log the token; only the numeric status if present.
    logInfo('[handoff] requestHandoffToken failed', {
      hasError: true,
      status: typeof status === 'number' ? status : null,
    });
    return null;
  }
}
